import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Grid } from '@mui/material';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import FormTextField from '../../../Components/Form/FormTextField';
import FormSelect from '../../../Components/Form/FormSelect';
import MassiveDatePicker from '../../MassiveMessages/MassiveDialog/Inputs/MassiveDatePicker';
import CustomDialog from '../../../Components/Dialog/CustomDialog';

const BranchSourceDialog = function BranchSourceDialog({
  open,
  handleClose,
  onSubmit,
  branchSourceData = {},
}) {
  const intl = useIntl();
  const defaultValues = {
    branchSourceId: null,
    branchId: null,
    sourceName: null,
    direction: true,
    sheetName: null,
    fileId: null,
    rootId: null,
    cellRange: null,
    completedOn: null,
    frequency: null,
  };

  const methods = useForm({
    mode: 'onChange',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = methods;

  const fields = [
    {
      name: 'sourceName',
      label: intl.messages.SOURCENAME,
      type: 'text',
      pattern: /^[A-Za-z\s-]+$/,
      patternMessage: intl.messages.INVALID_SOURCENAME,
      required: true,
    },
    {
      name: 'direction',
      label: intl.messages.DIRECTION,
      type: 'select',
      options: [
        { label: intl.messages.INPUT, value: true },
        { label: intl.messages.OUTPUT, value: false },
      ],
      required: true,
    },
    {
      name: 'fileId',
      label: intl.messages.FILEID,
      type: 'text',
      required: true,
    },
    {
      name: 'sheetName',
      label: intl.messages.SHEETNAME,
      type: 'text',
      required: true,
    },
    {
      name: 'completedOn',
      label: intl.messages.COMPLETEDON,
      type: 'date',
      required: true,
    },
    {
      name: 'frequency',
      label: intl.messages.FREQUENCY,
      type: 'number',
      required: true,
    },
    {
      name: 'cellRange',
      label: intl.messages.CELLRANGE,
      type: 'text',
      pattern:
        /^([A-Z]{1,3}[1-9][0-9]*(?::[A-Z]{1,3}[1-9][0-9]*)?)(\s*,\s*[A-Z]{1,3}[1-9][0-9]*(?::[A-Z]{1,3}[1-9][0-9]*)?)*$/,
      patternMessage: intl.messages.INVALID_CELLRANGE,
      required: true,
    },
    {
      name: 'rootId',
      label: intl.messages.ROOTID,
      type: 'text',
    },
  ];

  useEffect(() => {
    if (open) {
      const initialData = {
        ...branchSourceData,
        completedOn: branchSourceData?.completedOn
          ? new Date(branchSourceData.completedOn.toLocaleString())
          : null,
      };
      reset(initialData);
    }
  }, [open, branchSourceData, reset]);

  const onFormSubmit = (data) => {
    const formattedData = {
      ...data,
      completedOn: data.completedOn
        ? new Date(data.completedOn).toISOString()
        : null,
    };
    onSubmit(formattedData);
    reset(defaultValues);
  };

  const renderField = ({
    name,
    label,
    type,
    required,
    pattern,
    patternMessage,
    options,
  }) => {
    if (type === 'select') {
      return (
        <FormSelect
          name={name}
          label={label}
          required={required}
          error={errors[name]}
          options={options}
          noTranslation
        />
      );
    }

    if (type === 'date') {
      const rules = {
        required: required && `${label} ${intl.messages.REQUIRED_FIELD}`,
      };
      return (
        <Controller
          name={name}
          control={control}
          rules={rules}
          fullWidth
          render={({ field }) => (
            <MassiveDatePicker
              label={label}
              value={field.value}
              onChange={field.onChange}
              time
              sx={{ width: '100%' }}
            />
          )}
        />
      );
    }

    const validateCallback = pattern
      ? (value) => {
          if (!value) return true;
          return pattern.test(value) || patternMessage;
        }
      : undefined;

    return (
      <FormTextField
        name={name}
        label={label}
        type={type}
        required={required}
        error={errors[name]}
        validateCallback={validateCallback}
      />
    );
  };

  return (
    <FormProvider {...methods}>
      <CustomDialog
        title={intl.messages.EDIT_BRANCHSOURCE}
        open={open}
        close={(_, reason) => {
          if (reason !== 'clickaway' && reason !== 'escapeKeyDown') {
            handleClose();
            reset(defaultValues);
          }
        }}
        disableEscapeKeyDown
        messageDialog
        maxWidth="sm"
        fullWidth
        onSubmit={handleSubmit(onFormSubmit)}
        submitDisabled={!isValid}
        noBackdrop
        noForm
      >
        <Grid container spacing={1}>
          {fields.map((field) => (
            <Grid size={{ sm: 6, xs: 12 }} key={field.name}>
              {renderField(field)}
            </Grid>
          ))}
        </Grid>
      </CustomDialog>
    </FormProvider>
  );
};

export default BranchSourceDialog;
